import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Badge,
  Spinner,
  Row,
  Col,
  InputGroup,
  Form,
  Button
} from "react-bootstrap";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { getFirestore } from "firebase/firestore";
import { 
  FaHistory, 
  FaUserTie, 
  FaCalendarAlt, 
  FaInfoCircle, 
  FaSearch, 
  FaFilter,
  FaFileExcel,
  FaChartLine,
  FaTasks
} from "react-icons/fa";
import * as XLSX from "xlsx";

const db = getFirestore(firebaseApp);

function HistorialCambios({ usuario }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [moduloFiltro, setModuloFiltro] = useState("");
  const [accionFiltro, setAccionFiltro] = useState("");

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        // Obtenemos hasta 1000 registros para permitir una buena búsqueda local
        const q = query(collection(db, "audit_logs"), orderBy("timestamp", "desc"), limit(1000));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Filtramos por empresa a nivel de cliente para evitar requerir índices compuestos inmediatos
        const companyLogs = data.filter(log => !log.rucEmpresa || log.rucEmpresa === usuario.ruc);
        setLogs(companyLogs);
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  const formatDetalles = (detalles) => {
    if (!detalles) return <span className="text-muted">-</span>;

    const renderValue = (val) => {
      if (val === null || val === undefined) return "N/A";
      if (typeof val === 'object') {
        return JSON.stringify(val);
      }
      return String(val);
    };

    const keyMap = {
      dniEditado: "DNI Afectado",
      emailEditado: "Email Afectado",
      idDocumento: "ID Documento",
      declaracionId: "ID Declaración",
      origenEliminacion: "Origen Eliminación",
      documentosSeleccionados: "Docs. Seleccionados"
    };

    return (
      <div className="d-flex flex-column gap-2" style={{ maxWidth: "400px" }}>
        {Object.entries(detalles).map(([key, value]) => {
          if (key === 'cambiosRealizados' && typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="border rounded p-2 bg-white shadow-sm">
                <div className="fw-bold mb-2 text-primary" style={{ fontSize: "0.85rem" }}>
                  <FaInfoCircle className="me-1" /> Cambios Realizados:
                </div>
                <ul className="mb-0 ps-3 list-unstyled" style={{ fontSize: "0.85rem" }}>
                  {Object.entries(value).map(([campo, cambios]) => (
                    <li key={campo} className="mb-2 pb-1 border-bottom">
                      <div className="fw-semibold text-dark mb-1">{campo}</div>
                      {cambios && typeof cambios === 'object' && ('anterior' in cambios || 'nuevo' in cambios) ? (
                        <div className="d-flex align-items-center flex-wrap gap-1">
                          <Badge bg="danger" className="text-white text-decoration-line-through fw-normal">
                            {renderValue(cambios.anterior)}
                          </Badge>
                          <span className="text-muted mx-1">→</span>
                          <Badge bg="success" className="text-white fw-normal">
                            {renderValue(cambios.nuevo)}
                          </Badge>
                        </div>
                      ) : (
                        <div className="text-muted">{renderValue(cambios)}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }

          // Otras propiedades del log (dniEditado, idDocumento, etc)
          const mappedKey = keyMap[key] || key;
          return (
            <div key={key} className="d-flex align-items-center mt-1" style={{ fontSize: "0.85rem" }}>
              <Badge bg="light" text="dark" className="me-2 border fw-medium">
                {mappedKey}
              </Badge>
              <span className="text-secondary text-truncate" title={renderValue(value)}>
                {renderValue(value)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Filtrado de registros
  const filteredLogs = logs.filter(log => {
    const term = searchTerm.toLowerCase();
    const detallesString = log.detalles ? JSON.stringify(log.detalles).toLowerCase() : "";
    
    const matchesSearch = 
      (log.adminNombre && log.adminNombre.toLowerCase().includes(term)) ||
      (log.adminEmail && log.adminEmail.toLowerCase().includes(term)) ||
      (log.adminDni && log.adminDni.includes(term)) ||
      (log.accion && log.accion.toLowerCase().includes(term)) ||
      detallesString.includes(term);
      
    const matchesModulo = moduloFiltro ? log.modulo === moduloFiltro : true;
    const matchesAccion = accionFiltro ? log.accion === accionFiltro : true;

    return matchesSearch && matchesModulo && matchesAccion;
  });

  // Estadísticas rápidas
  const todayLogs = logs.filter(log => {
    if (!log.timestamp) return false;
    const date = new Date(log.timestamp.seconds * 1000);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }).length;

  const editActions = logs.filter(log => log.accion && log.accion.includes("EDICI")).length;

  // Obtener valores únicos para filtros
  const modulosUnicos = [...new Set(logs.map(log => log.modulo).filter(Boolean))];
  const accionesUnicas = [...new Set(logs.map(log => log.accion).filter(Boolean))];

  // Exportar a Excel
  const exportToExcel = () => {
    const dataToExport = filteredLogs.map((log) => {
      const fecha = log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString('es-PE') : "Sin fecha";
      const baseRow = {
        "Fecha y Hora": fecha,
        "Módulo": log.modulo || "",
        "Acción": log.accion || "",
        "DNI Administrador": log.adminDni || "",
        "Nombre Administrador": log.adminNombre || "",
        "Email Administrador": log.adminEmail || "",
        "Rol Administrador": log.adminRol || "",
        "RUC Empresa": log.rucEmpresa || ""
      };

      // Mapeo de claves para el Excel
      const keyMapExcel = {
        dniEditado: "DNI Afectado",
        emailEditado: "Email Afectado",
        idDocumento: "ID Documento",
        declaracionId: "ID Declaración",
        origenEliminacion: "Origen Eliminación",
        documentosSeleccionados: "Docs. Seleccionados"
      };

      // Descomponer detalles en columnas individuales
      if (log.detalles) {
        Object.entries(log.detalles).forEach(([key, value]) => {
          if (key === 'cambiosRealizados' && typeof value === 'object' && value !== null) {
            // Extraer cada campo modificado como columnas "Anterior" y "Nuevo"
            Object.entries(value).forEach(([campo, cambios]) => {
              if (cambios && typeof cambios === 'object' && ('anterior' in cambios || 'nuevo' in cambios)) {
                baseRow[`Cambio: ${campo} (Anterior)`] = typeof cambios.anterior === 'object' ? JSON.stringify(cambios.anterior) : String(cambios.anterior ?? "N/A");
                baseRow[`Cambio: ${campo} (Nuevo)`] = typeof cambios.nuevo === 'object' ? JSON.stringify(cambios.nuevo) : String(cambios.nuevo ?? "N/A");
              } else {
                baseRow[`Cambio: ${campo}`] = typeof cambios === 'object' ? JSON.stringify(cambios) : String(cambios ?? "N/A");
              }
            });
          } else {
            // Otros campos técnicos extra
            const mappedKey = keyMapExcel[key] || key;
            baseRow[`Detalle: ${mappedKey}`] = typeof value === 'object' ? JSON.stringify(value) : String(value ?? "N/A");
          }
        });
      }

      return baseRow;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Auditoría");
    XLSX.writeFile(wb, `Auditoria_Historial_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              borderBottom: "3px solid #f59e0b",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaHistory className="me-3" />
                Auditoría y Trazabilidad
              </h2>
              <p className="mb-0 opacity-75">
                Historial completo de cambios críticos realizados en la plataforma
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            
            {/* Barra superior de Exportación */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1} className="text-center">
                    <div className="rounded-3 p-3 d-inline-block" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white" }}>
                      <FaChartLine size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Monitor de Operaciones</h5>
                    <p className="text-muted mb-0 small">
                      Visualiza, filtra y audita las acciones de los administradores y usuarios con permisos.
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end mt-3 mt-md-0">
                    <Button
                      variant="outline-success"
                      className="rounded-pill px-4 border-2 shadow-sm fw-bold"
                      onClick={exportToExcel}
                      disabled={filteredLogs.length === 0}
                    >
                      <FaFileExcel className="me-2" /> Exportar a Excel
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas Rápidas */}
            <Row className="mb-4">
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white", borderRadius: "15px" }}>
                    <FaHistory size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">{logs.length}</h4>
                    <p className="mb-0 small opacity-75">Total Registros (Últimos)</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                    <FaCalendarAlt size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">{todayLogs}</h4>
                    <p className="mb-0 small opacity-75">Acciones Hoy</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", color: "white", borderRadius: "15px" }}>
                    <FaTasks size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">{editActions}</h4>
                    <p className="mb-0 small opacity-75">Ediciones (Modificaciones)</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Filtros y Búsqueda */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="g-3 align-items-center">
                  <Col md={6}>
                    <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                      <InputGroup.Text className="border-0 px-4" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white" }}>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        className="border-0 py-3"
                        placeholder="Buscar por DNI, Nombre o Acción..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ backgroundColor: "white" }}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                      <InputGroup.Text className="border-0 bg-white text-secondary">
                        <FaFilter />
                      </InputGroup.Text>
                      <Form.Select 
                        className="border-0 py-3 text-secondary"
                        value={moduloFiltro}
                        onChange={(e) => setModuloFiltro(e.target.value)}
                      >
                        <option value="">Todos los Módulos</option>
                        {modulosUnicos.map((mod, idx) => (
                          <option key={idx} value={mod}>{mod}</option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                      <InputGroup.Text className="border-0 bg-white text-secondary">
                        <FaFilter />
                      </InputGroup.Text>
                      <Form.Select 
                        className="border-0 py-3 text-secondary"
                        value={accionFiltro}
                        onChange={(e) => setAccionFiltro(e.target.value)}
                      >
                        <option value="">Todas las Acciones</option>
                        {accionesUnicas.map((acc, idx) => (
                          <option key={idx} value={acc}>{acc}</option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Tabla de Resultados */}
            <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
                  <p className="mt-3 text-muted fw-bold">Cargando registros de auditoría...</p>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                  <Table hover className="align-middle mb-0 bg-white" style={{ fontSize: "0.95rem" }}>
                    <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6", position: "sticky", top: 0, zIndex: 10 }}>
                      <tr>
                        <th className="py-3 px-4 text-secondary fw-semibold" style={{ minWidth: "160px" }}><FaCalendarAlt className="me-2" />Fecha y Hora</th>
                        <th className="py-3 text-secondary fw-semibold">Módulo</th>
                        <th className="py-3 text-secondary fw-semibold">Acción</th>
                        <th className="py-3 text-secondary fw-semibold"><FaUserTie className="me-2" />Autor</th>
                        <th className="py-3 px-4 text-secondary fw-semibold" style={{ minWidth: "300px" }}><FaInfoCircle className="me-2" />Detalles Técnicos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-5 text-muted">
                            <FaHistory size="3rem" className="text-muted opacity-25 mb-3 d-block mx-auto" />
                            <h5>No se encontraron registros de auditoría.</h5>
                            <p>Intenta cambiar los filtros o el término de búsqueda.</p>
                          </td>
                        </tr>
                      ) : (
                        filteredLogs.map(log => (
                          <tr key={log.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                            <td className="px-4 text-muted fw-medium" style={{ whiteSpace: "nowrap" }}>
                              {log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString('es-PE', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit', second: '2-digit'
                              }) : "Sin fecha"}
                            </td>
                            <td>
                              <Badge bg="secondary" className="px-3 py-2 rounded-pill" style={{ letterSpacing: "0.5px" }}>
                                {log.modulo}
                              </Badge>
                            </td>
                            <td>
                              <Badge 
                                bg={log.accion.includes("ELIMINA") ? "danger" : log.accion.includes("EDICI") ? "warning" : "success"}
                                text={log.accion.includes("EDICI") ? "dark" : "white"}
                                className="px-3 py-2 rounded-pill"
                                style={{ letterSpacing: "0.5px" }}
                              >
                                {log.accion}
                              </Badge>
                            </td>
                            <td>
                              <div className="fw-bold text-dark">{log.adminNombre !== "desconocido" ? log.adminNombre : log.adminEmail}</div>
                              <div className="text-muted small">DNI: <span className="fw-semibold">{log.adminDni}</span></div>
                              {log.adminRol && log.adminRol !== "desconocido" && (
                                <Badge bg="light" text="dark" className="mt-1 border" style={{ fontSize: "0.75rem", fontWeight: "500", color: "#475569" }}>
                                  {log.adminRol.toUpperCase()}
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {formatDetalles(log.detalles)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card>

          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default HistorialCambios;
